import type { StandardDivProps } from '@sprinx/react-mui-components';
import { createStyles, IconButton, makeStyles, Tooltip, type Theme } from '@material-ui/core';
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useController } from 'react-hook-form';
import Cancel from '@material-ui/icons/Cancel';
import { ProductParameterTypesWithValueCounts } from '../../../api/products/productParameterTypes';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

export type CataloguePageMainFilterParamClassKey =
  | 'root'
  | 'filterElementClearButton'
  | 'filterElementName'
  | 'filterElementValues'
  | 'filterElementValue'
  | 'filterElementValueCheckbox'
  | 'filterElementValueName'
  | 'filterElementValueCount';

export interface CataloguePageMainFilterParamProps extends StandardDivProps<CataloguePageMainFilterParamClassKey> {
  control: any;
  onChangeSubmit: () => void;
  param: ProductParameterTypesWithValueCounts;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles<CataloguePageMainFilterParamClassKey, {}>({
    root: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1),
    },
    filterElementClearButton: {
      margin: theme.spacing(-2.25),
    },
    filterElementName: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    filterElementValues: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'top',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
      listStyleType: 'none',
      paddingLeft: 0,
    },
    filterElementValue: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
      alignItems: 'flex-start',
    },
    filterElementValueCheckbox: {
      position: 'absolute',
      opacity: 0,
      width: 0,
      height: 0,
      '&:checked + label::before': {
        backgroundColor: '#000',
        borderColor: '#000',
      },
      '&:checked + label::after': {
        content: '""',
        position: 'absolute',
        left: 6,
        top: 4,
        width: 6,
        height: 10,
        border: 'solid white',
        borderWidth: '0 2px 2px 0',
        transform: 'rotate(45deg)',
      },
    },
    filterElementValueName: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'space-between',
      position: 'relative',
      paddingLeft: 28,
      wordBreak: 'break-word',

      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 3,
        width: '1rem',
        height: '1rem',
        padding: '0.25rem',
        border: '1px solid #000',
        backgroundColor: '#fff',
        boxSizing: 'border-box',
        transition: 'all 0.25s ease-in-out',
      },
      '&:hover::before': {
        borderColor: '#000',
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
      },
    },
    filterElementValueCount: {
      width: theme.spacing(3),
      textAlign: 'right',
      wordBreak: 'keep-all',
    },
  }),
);

export function CataloguePageMainFilterParam({
  className,
  control,
  param,
  onChangeSubmit,
  classes: pClasses,
}: CataloguePageMainFilterParamProps): JSX.Element {
  const classes = useStyles({ classes: pClasses });
  const t = useTranslateWithFallback();
  const { field } = useController({
    name: param.code,
    control,
    defaultValue: [],
  });
  const fieldValue = field.value || [];
  const isActive = fieldValue && fieldValue.filter(Boolean).length > 0;
  const unit = param.units && param.units.length > 0 ? ` ${param.units[0]}` : '';

  const handleResetFilterParam = useCallback(() => {
    field.onChange([]);
    onChangeSubmit();
  }, [field, onChangeSubmit]);

  return (
    <div key={param.code} className={clsx(classes.root, className)}>
      <div className={classes.filterElementName}>
        <span>{t(param.label)}</span>
        {isActive && (
          <Tooltip title={t('common/resetFilterParam', { paramName: t(param.label) })}>
            <IconButton color={'inherit'} onClick={handleResetFilterParam} className={classes.filterElementClearButton}>
              <Cancel />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <div className={classes.filterElementValues}>
        {param.valueCounts.map(({ value, count }, idx) => (
          <div key={idx} className={classes.filterElementValue}>
            <input
              id={`checkbox-${param.code}-${idx}`}
              type='checkbox'
              name={param.code}
              onChange={(e) => {
                const valueCopy = [...fieldValue];
                valueCopy[idx] = e.target.checked ? e.target.value : null;
                field.onChange(valueCopy);
              }}
              value={value}
              checked={fieldValue.includes(value)}
              className={classes.filterElementValueCheckbox}
            />
            <label htmlFor={`checkbox-${param.code}-${idx}`} className={classes.filterElementValueName}>
              <span>
                {value}
                {unit}
              </span>
              <span className={classes.filterElementValueCount}>{count}</span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
